import React, { useMemo } from "react";
import {
	Accordion as ChakraAccordion,
	AccordionItem,
	AccordionButton,
	AccordionPanel,
	AccordionIcon,
	Badge,
	Text,
} from "@chakra-ui/react";
import SkeletonBase from "newui/components/Skeleton";
import { useRevisionRequests } from "../hooks/useRevisionRequests";
import { useRevisionUploads } from "../hooks/useRevisionUploads";
import { RevisionUploadAccordion } from "./RevisionUploadAccordion";
import type {
	EvaluationRevisionRequest,
	EvaluationRevisionUpload,
	EvaluationRevisionSupplier,
} from "../types/revision-review.type";

interface PanelRevisionsViewProps {
	evaluationId: number;
	rfxId: number;
}

interface SupplierRevisionData {
	hasRevisedQuote: boolean;
	supplier: EvaluationRevisionSupplier;
	uploads: EvaluationRevisionUpload[];
}

function buildSupplierRevisionData(
	requests: EvaluationRevisionRequest[],
	uploads: EvaluationRevisionUpload[]
): SupplierRevisionData[] {
	const supplierMap = new Map<number, SupplierRevisionData>();

	const qualifyingStatuses = new Set(["approved", "completed"]);

	requests
		.filter((r) => qualifyingStatuses.has(r.status))
		.forEach((request) => {
			const existing = supplierMap.get(request.rfx_invite_id);
			if (existing) {
				if (request.type === "quote") {
					existing.hasRevisedQuote = true;
				}
			} else {
				supplierMap.set(request.rfx_invite_id, {
					supplier: request.supplier,
					hasRevisedQuote: request.type === "quote",
					uploads: [],
				});
			}
		});

	uploads.forEach((upload) => {
		const existing = supplierMap.get(upload.rfx_invite_id);
		if (existing) {
			existing.uploads.push(upload);
		} else {
			supplierMap.set(upload.rfx_invite_id, {
				supplier: upload.supplier,
				hasRevisedQuote: false,
				uploads: [upload],
			});
		}
	});

	return Array.from(supplierMap.values()).sort((a, b) =>
		a.supplier.name.localeCompare(b.supplier.name)
	);
}

/**
 * Whether this view has anything to display.
 *
 * The panel-member guided tour anchors on `panel-revisions-view`, which is not
 * rendered while `supplierData` is empty (the empty state renders instead), so
 * "Show Me" was a dead button with nothing to point at. RevisionsStep uses this to
 * withhold the tour steps entirely, which hides the button.
 *
 * Deliberately reuses the same queries and the same `buildSupplierRevisionData` as
 * the component rather than re-deriving availability — the react-query cache makes
 * the duplicate calls free. Mirrors `useRevisionRequestsPendingCount`.
 */
export const usePanelRevisionsHasData = (
	rfxId: number,
	evaluationId: number
): boolean => {
	const { data: requests = [] } = useRevisionRequests(rfxId, evaluationId);
	const { data: uploads = [] } = useRevisionUploads(rfxId, evaluationId);

	return useMemo(
		() => buildSupplierRevisionData(requests, uploads).length > 0,
		[requests, uploads]
	);
};

export const PanelRevisionsView: React.FC<PanelRevisionsViewProps> = ({
	rfxId,
	evaluationId,
}) => {
	const {
		data: requests = [],
		isLoading: requestsLoading,
	} = useRevisionRequests(rfxId, evaluationId);

	const {
		data: uploads = [],
		isLoading: uploadsLoading,
	} = useRevisionUploads(rfxId, evaluationId);

	const isLoading = requestsLoading || uploadsLoading;

	const supplierData = useMemo(
		() => buildSupplierRevisionData(requests, uploads),
		[requests, uploads]
	);

	if (isLoading) {
		return (
			<div className="space-y-2" data-testid="panel-revisions-loading">
				<SkeletonBase height="60px" width="100%" />
				<SkeletonBase height="60px" width="100%" />
				<SkeletonBase height="60px" width="100%" />
			</div>
		);
	}

	if (supplierData.length === 0) {
		return (
			<div
				className="rounded-lg border border-dashed border-gray-300 p-8 text-center"
				data-testid="panel-revisions-empty"
			>
				<Text className="text-sm text-gray-400">
					No revisions available for this evaluation
				</Text>
			</div>
		);
	}

	return (
		<div data-testid="panel-revisions-view">
			<ChakraAccordion allowMultiple data-testid="panel-revisions-accordion">
				{supplierData.map((item) => (
					<AccordionItem
						key={item.supplier.id}
						className="mb-2 rounded-lg border border-gray-200"
						border="none"
					>
						<AccordionButton className="rounded-lg px-4 py-3 hover:bg-gray-50">
							<div className="flex flex-1 items-center gap-2 text-left">
								<Text className="text-sm font-medium text-gray-800">
									{item.supplier.name}
								</Text>
								{item.hasRevisedQuote && (
									<Badge
										colorScheme="blue"
										variant="subtle"
										className="text-xs"
										data-testid={`revised-quote-badge-${item.supplier.id}`}
									>
										Has Revised Quote
									</Badge>
								)}
							</div>
							<AccordionIcon />
						</AccordionButton>
						<AccordionPanel className="px-4 pb-4 pt-0">
							{item.uploads.length > 0 ? (
								<RevisionUploadAccordion uploads={item.uploads} />
							) : (
								<Text
									className="py-4 text-center text-sm text-gray-400"
									data-testid={`no-uploads-${item.supplier.id}`}
								>
									No revision files uploaded
								</Text>
							)}
						</AccordionPanel>
					</AccordionItem>
				))}
			</ChakraAccordion>
		</div>
	);
};
